import React from 'react';
import { IconSearch, IconDownload, IconEye } from '@tabler/icons-react';
import { Link } from 'react-router';

const PaymentHistoryTable = ({
  paymentHistory,
  searchTerm,
  onSearchChange,
}) => {
  // Helper functions
  const formatPrice = (amount) => (amount / 100).toFixed(2);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);

    if (isNaN(date.getTime())) return 'Invalid Date';

    try {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
    } catch (e) {
      return date.toDateString();
    }
  };

  const getStatusClass = (status) => {
    if (status === 'paid') return 'paid';
    if (status === 'pending') return 'pending';
    return 'failed';
  };

  // Render table rows - no loading state needed, parent gates rendering
  const renderTableRows = () => {
    if (!paymentHistory || paymentHistory.length === 0) {
      return (
        <tr>
          <td colSpan="6">
            <div className="empty-state">
              <p>No payment history found</p>
            </div>
          </td>
        </tr>
      );
    }

    return paymentHistory.map((payment) => (
      <tr key={payment.id}>
        <td data-label="Invoice #">{payment.number ?? '-'}</td>
        <td data-label="Date">{formatDate(payment.created)}</td>
        <td data-label="Amount">${formatPrice(payment.amount_paid)}</td>
        <td data-label="Status">
          <span className={`status-badge ${getStatusClass(payment.status)}`}>
            {payment.status}
          </span>
        </td>
        <td data-label="Payment Method">
          •••• {payment?.default_payment_method_last4 ?? '••••'}
        </td>
        <td data-label="Action">
          <div className="invoice-actions">
            {/* View Invoice Button */}
            <button
              className="invoice-action-btn"
              disabled={!payment.hosted_invoice_url && !payment.invoice_pdf}
              title="View Invoice"
            >
              <Link
                style={{ color: 'inherit', textDecoration: 'none' }}
                to={payment.hosted_invoice_url || payment.invoice_pdf}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (!payment.hosted_invoice_url && !payment.invoice_pdf) {
                    e.preventDefault();
                  }
                }}
              >
                <IconEye size={18} />
              </Link>
            </button>

            {/* Download Invoice Button */}
            <button
              className="invoice-action-btn"
              disabled={!payment.invoice_pdf}
              title="Download Invoice"
            >
              <Link
                style={{ color: 'inherit', textDecoration: 'none' }}
                to={payment.invoice_pdf}
                target="_blank"
                download
                onClick={(e) => {
                  if (!payment.invoice_pdf) e.preventDefault();
                }}
              >
                <IconDownload size={18} />
              </Link>
            </button>
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <div className="payment-history-section">
      <div className="payment-history-header">
        <div className="section-header">
          <h3>Payment History</h3>
          <p>View and download past invoices</p>
        </div>

        <div className="payment-history-search">
          <div className="payment-search-input">
            <IconSearch className="search-icon" size={18} />
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={onSearchChange}
            />
          </div>
        </div>
      </div>

      <div className="invoice-table-wrapper">
        <table className="invoice-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Payment Method</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>{renderTableRows()}</tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentHistoryTable;
